import {
  createElement,
  type AnchorHTMLAttributes,
  type ComponentType,
  type ImgHTMLAttributes,
} from "react";

export type GroundImagePrimitiveProps = ImgHTMLAttributes<HTMLImageElement> & {
  alt: string;
  imageClassName?: string;
  priority?: boolean;
  src: string;
};

export type GroundLinkPrimitiveProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href: string;
};

export type GroundPrimitiveRegistry = {
  Image: ComponentType<GroundImagePrimitiveProps>;
  Link: ComponentType<GroundLinkPrimitiveProps>;
};

export type GroundPrimitiveName = keyof GroundPrimitiveRegistry;

export interface GroundFormSubmissionField {
  label: string;
  type: "short-text" | "email" | "phone" | "long-text" | "select" | "checkbox";
  value: string | boolean;
}

export interface GroundFormSubmission {
  fields: GroundFormSubmissionField[];
  sourcePath: string;
}

export type GroundFormSubmitter = (submission: GroundFormSubmission) => Promise<void>;

const Image = ({ imageClassName, priority: _priority, ...props }: GroundImagePrimitiveProps) =>
  createElement("img", {
    ...props,
    className: [props.className, imageClassName].filter(Boolean).join(" ") || undefined,
  });

const Link = ({ children, rel, target, ...props }: GroundLinkPrimitiveProps) =>
  createElement(
    "a",
    { ...props, rel: target === "_blank" && !rel ? "noopener noreferrer" : rel, target },
    children,
  );

const registry: GroundPrimitiveRegistry = { Image, Link };
let formSubmitter: GroundFormSubmitter | undefined;

export function registerComponent<K extends GroundPrimitiveName>(
  name: K,
  component: GroundPrimitiveRegistry[K],
) {
  registry[name] = component;
}

export function getComponent<K extends GroundPrimitiveName>(name: K): GroundPrimitiveRegistry[K] {
  return registry[name];
}

export function registerFormSubmitter(submitter: GroundFormSubmitter) {
  formSubmitter = submitter;
}

export async function submitForm(submission: GroundFormSubmission) {
  if (!formSubmitter) throw new Error("Form submission is not configured");
  await formSubmitter(submission);
}
